import { Component, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { NavigationMenuData, NavigationModule } from '@vg-constellation/angular-20/navigation';
import { BehaviorSubject, combineLatestWith, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AccountsUsersDto } from 'src/common/dtos/accounts-users.dto';
import { environment } from 'src/environments/environment';

import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { ZciNavigationItemId, ZciNavigationService } from '../../../services/zci-navigation';
import { getLoadingState, selectAccountUsers, selectOwnAccounts } from '../../../store/selectors/account-permissions.selector';
import { ApiState } from '../../../store/state/api.state';
import { ContentUtil } from '../../../utils/content/content.util';
import { PageComponent } from '../page.component';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';
import { ClientSegmentFacadeService } from '../../../services/client-segment-observer/client-segment.service';
import { ZciCookieService } from '../../../services/zci-cookie/zci-cookie.service';
import CONSTANTS from '../../../../common/constants/constants';
import { LoggerService } from '../../../services/logger/logger.service';
import { AsyncPipe, NgIf } from '@angular/common';
import { InternalCalloutModule } from '@vg-constellation/angular-20/internal-callout';
import { LinkModule } from '@vg-constellation/angular-20/link';
import { AdvisorComponent } from '../../representative-card/advisor/advisor.component';
import { TileComponent } from '../../tiles/tile.component';
import { LoadingDirective } from '../../../directives/loading.directive';
import { WhatWouldYouLikeToDoBannerComponent } from '../../what-would-you-like-to-do-banner/what-would-you-like-to-do-banner.component';

@Component({
    selector: 'zci-account-permissions-page',
    templateUrl: './account-permissions-page.component.html',
    imports: [
        AsyncPipe,
        NgIf,
        RouterLink,
        RouterOutlet,
        NavigationModule,
        InternalCalloutModule,
        LinkModule,
        TileComponent,
        AdvisorComponent,
        LoadingDirective,
        WhatWouldYouLikeToDoBannerComponent,
    ],
})
export class AccountPermissionsPageComponent extends PageComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private injector = inject(Injector);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);
    private readonly zciCookieService = inject(ZciCookieService);


    appRoutes = APPLICATION_ROUTES;

    content = ContentUtil.content;

    contactPhone: string = ContentUtil.globalRMSupportContactPhoneNumber;

    get internalOnlyInfoBoxText(): string {
        return ContentUtil.content.home.internalOnlyInfoBoxText.replace('{{legacyCraveUrl}}.', '');
    }

    get legacyCraveUrl(): string {
        const legacyPort = this.zciCookieService.legacySatCookie || CONSTANTS.DEFAULT_NON_PROD_PORT;
        return environment.LEGACY_CRAVE_URL.replace('{{legacyPort}}', legacyPort);
    }

    legacyCraveUrlText: string = ContentUtil.content.home.legacyCraveUrlText;

    backHref = environment.ACCOUNT_SETTINGS_URL;

    currentNavItem$: BehaviorSubject<ZciNavigationItemId | null> =
        new BehaviorSubject<ZciNavigationItemId | null>(null);

    initNavItem$: Observable<ZciNavigationItemId | null> = this.store
        .select(selectAccountUsers)
        .pipe(
            combineLatestWith(this.activatedRoute.queryParams),
            map(([{ data }]) =>
                this.zciNavigationService.getInitSelectedNavItem(data, this.router.url),
            ),
            tap((initNavItem: ZciNavigationItemId | null) => {
                /* istanbul ignore next */
                const initURL: string = APPLICATION_ROUTES[initNavItem] ?? '';
                this.router.navigate([initURL]);
                this.currentNavItem$.next(initNavItem);
            }),
        );

    navMenuData$: Observable<NavigationMenuData> = this.store.select(selectAccountUsers).pipe(
        map(({ data }: ApiState<AccountsUsersDto>) => data),
        map((data: AccountsUsersDto) => this.zciNavigationService.getAvailableNavItems(data)),
    );


    isLoading$: Observable<boolean> = this.store.select(getLoadingState);

    hasOwnAccounts$: Observable<boolean> = this.store.select(selectOwnAccounts).pipe(
        map((accounts) => accounts?.length > 0),
    );

    isWealthManagementClient$: Observable<boolean> = this.clientSegmentFacadeService.isWealthManagementClient$;

    isInternal: boolean = environment.isInternal;

    private readonly zciNavigationService: ZciNavigationService;
    private readonly loggerService = inject(LoggerService);

    private destroy$ = new Subject<void>();

    phoneNumber: string;
    supportName: string;

    constructor() {
        super();
        this.zciNavigationService = this.injector.get<ZciNavigationService>(ZciNavigationService);
    }

    ngOnInit(): void {
        this.clientSegmentFacadeService.clientSegmentContent$
            .pipe(takeUntil(this.destroy$))
            .subscribe((content: ClientSegmentContentDTO) => {
                this.phoneNumber = this.contactPhone.replace('${phoneNumber}', content?.phoneNumber)
                this.supportName = content?.supportTeam.replace(' team', '')
            });
    }

    handleNavItemChange(navItem: string): void {
        this.currentNavItem$.next(<ZciNavigationItemId>navItem);
    }

    openLegacyCraveUrl(): void {
        this.loggerService.info('legacy crave access permissions url was clicked');
        window.open(this.legacyCraveUrl, '_blank');
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
